<template>
  <!--
    AB#21966 / AB#21967 — Team chat: the order's internal conversation between
    CSMs and order admins. Never shown to the customer or the factory. Opens
    as a section of the order like Ask ELSA; polls while open.
  -->
  <section class="tct" data-test="team-chat">
    <header class="tct-head">
      <div class="tct-title">
        <h2>Team chat</h2>
        <span class="tct-sub">
          <LockClosedIcon class="size-3" aria-hidden="true" />
          Internal only. The customer and factory never see this.
        </span>
      </div>
      <div v-if="state" class="tct-people" aria-label="People on this chat">
        <span
          v-for="m in shownMembers"
          :key="m.user_id"
          class="tct-person"
          :title="`${m.name}${m.role ? ` · ${m.role}` : ''}${m.online ? ' · online' : ''}${m.following ? '' : ' · not following'}`"
        >
          <UserAvatar
            :name="m.name"
            :initials="m.initials"
            :src="m.avatar_url ?? null"
            :size="26"
            tone="soft"
          />
          <span v-if="m.online" class="tct-online" aria-hidden="true"></span>
        </span>
        <span v-if="moreMembers" class="tct-more">+{{ moreMembers }}</span>
      </div>
      <button v-if="state" type="button" class="tct-chip" data-test="tc-files" @click="openFiles">
        <DocumentIcon class="size-3.5" />Files · {{ state.files_count }}
      </button>
      <button
        v-if="state"
        type="button"
        class="tct-chip"
        :class="{ 'tct-chip--on': state.me.following }"
        :aria-pressed="state.me.following"
        data-test="tc-follow"
        :title="
          state.me.following
            ? 'You get unread counts and an email when away'
            : 'Follow to be notified'
        "
        @click="setFollowing(!state.me.following)"
      >
        <BellIcon class="size-3.5" />{{ state.me.following ? 'Following' : 'Follow' }}
      </button>
      <button
        v-if="state?.can.manage"
        type="button"
        class="tct-chip"
        data-test="tc-export"
        title="Download the chat as a spreadsheet"
        @click="doExport"
      >
        <ArrowDownTrayIcon class="size-3.5" />Export
      </button>
    </header>

    <div ref="scroller" class="tct-list" data-test="tc-list" @scroll="onScroll">
      <p v-if="loading && !state" class="tct-empty">Loading…</p>
      <p v-else-if="error && !state" class="tct-empty tct-err" role="alert">{{ error }}</p>
      <template v-else-if="state">
        <button v-if="state.has_more" type="button" class="tct-older" @click="older">
          Show earlier messages
        </button>

        <div v-if="legacy" class="tct-legacy" data-test="tc-legacy-note">
          <div class="tct-legacy-h">Internal note from before team chat</div>
          <p>{{ legacy.text }}</p>
          <span v-if="legacy.by">{{ legacy.by }}{{ legacy.at ? ` · ${legacy.at}` : '' }}</span>
        </div>

        <p v-if="!state.messages.length" class="tct-empty" data-test="tc-empty">
          No messages yet. Ask a question, share a file, or @mention someone. Everyone who can open
          this order with team chat access can read it.
        </p>

        <template v-for="row in rows" :key="row.key">
          <div v-if="row.type === 'day'" class="tct-day">
            <span>{{ row.label }}</span>
          </div>
          <div v-else-if="row.type === 'new'" class="tct-new" data-test="tc-new-line">
            <span>{{ row.label }}</span>
          </div>
          <TeamChatMessage
            v-else
            :message="row.message"
            :me-id="meId"
            :can-send="state.can.send"
            :can-translate="canTranslate"
            :names="names"
            :portions="state.portions"
            :action-finding-ids="row.actionFindingIds"
            :load-thumb="fileUrl"
            @reply="(m) => (replyTo = m)"
            @edit="onEdit"
            @delete="onDelete"
            @translate="translateMessage"
            @dismiss="onDismiss"
            @move="onMove"
            @open-file="openFile"
          />
        </template>

        <div v-if="state.typing.length" class="tct-typing" data-test="tc-typing">
          <span class="tct-dots" aria-hidden="true"><i></i><i></i><i></i></span>
          {{ typingText }}
        </div>
      </template>
    </div>

    <button
      v-if="newBelow > 0"
      type="button"
      class="tct-jump"
      data-test="tc-jump"
      @click="scrollToBottom(true)"
    >
      {{ newBelow }} new {{ newBelow === 1 ? 'message' : 'messages' }} ↓
    </button>

    <footer v-if="state" class="tct-foot">
      <p v-if="error" class="tct-err" role="alert">{{ error }}</p>
      <TeamChatComposer
        v-if="state.can.send"
        ref="composer"
        :order-no="state.order_no || orderNo"
        :sending="sending"
        :reply-to="replyTo"
        :portions="state.portions"
        :can-write="canWrite"
        :can-translate="canTranslate"
        @send="onSend"
        @typing="typing"
        @cancel-reply="replyTo = null"
      />
      <div v-else class="tct-readonly" data-test="tc-readonly">
        <EyeIcon class="size-5" aria-hidden="true" />
        <div>
          <b>You can read this chat</b>
          <span
            >Sending messages needs the “Send order team chat messages” permission. You are still
            notified when someone @mentions you.</span
          >
        </div>
      </div>
    </footer>

    <Drawer
      v-if="filesOpen"
      title="Files in this chat"
      :subtitle="`Order #${state?.order_no || orderNo}`"
      data-test="tc-files-drawer"
      @close="filesOpen = false"
    >
      <p v-if="filesLoading" class="tct-empty">Loading…</p>
      <p v-else-if="!fileList.length" class="tct-empty">No files yet. Attach one to a message.</p>
      <ul v-else class="tct-filelist">
        <li v-for="f in fileList" :key="f.id">
          <button type="button" class="tct-fileitem" @click="openFile(f)">
            <DocumentIcon class="size-5" aria-hidden="true" />
            <span class="tct-filename">{{ f.name }}</span>
            <span class="tct-filemeta"
              >{{ fileSize(f.size) }}{{ f.by ? ` · ${f.by}` : ''
              }}{{ f.at ? ` · ${ago(f.at)}` : '' }}</span
            >
          </button>
        </li>
      </ul>
    </Drawer>

    <Drawer
      v-if="moveAsk"
      title="Move to another order"
      :subtitle="`From order #${state?.order_no || orderNo}`"
      data-test="tc-move-drawer"
      @close="moveAsk = null"
    >
      <label class="tct-move-label" for="tc-move-order"
        >Which order are these messages about?</label
      >
      <input
        id="tc-move-order"
        ref="moveInput"
        v-model="moveAsk.orderNo"
        type="text"
        inputmode="numeric"
        autocomplete="off"
        class="tct-move-input"
        placeholder="Order number, e.g. 28100"
        data-test="tc-move-order"
        @keyup.enter="confirmMove"
      />
      <p class="tct-move-hint">
        {{
          moveMessages.length === 1
            ? 'This message leaves'
            : `These ${moveMessages.length} messages leave`
        }}
        this chat and appear in that order's chat, with a line in both saying who moved them. The
        people who wrote them follow that chat.
      </p>
      <ul v-if="moveMessages.length" class="tct-move-list" data-test="tc-move-list">
        <li v-for="m in moveMessages" :key="m.id">
          <b>{{ m.user?.name ?? 'Staff' }}</b>
          <span>{{ m.body_en || m.body }}</span>
        </li>
      </ul>
      <p v-if="moveAsk.error" class="tct-move-error" role="alert" data-test="tc-move-error">
        {{ moveAsk.error }}
      </p>
      <template #footer>
        <button type="button" class="dash-btn" data-test="tc-move-cancel" @click="moveAsk = null">
          Cancel
        </button>
        <button
          type="button"
          class="dash-btn dash-btn--primary"
          :disabled="!moveTarget || moving"
          data-test="tc-move-confirm"
          @click="confirmMove"
        >
          {{ moving ? 'Moving…' : moveTarget ? `Move to #${moveTarget}` : 'Move' }}
        </button>
      </template>
    </Drawer>
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
  ArrowDownTrayIcon,
  BellIcon,
  DocumentIcon,
  EyeIcon,
  LockClosedIcon,
} from '@heroicons/vue/24/outline';
import Drawer from '@/components/Shared/Drawer.vue';
import UserAvatar from '@/components/Shared/UserAvatar.vue';
import TeamChatComposer from './TeamChatComposer.vue';
import TeamChatMessage from './TeamChatMessage.vue';
import { showToast } from '@/composables/swal';
import { useMainStore } from '@/store';
import {
  ago,
  dayLabel,
  fileSize,
  useTeamChat,
  type TcFile,
  type TcMessage,
} from '@/composables/useTeamChat';

const props = withDefaults(
  defineProps<{
    orderId: number;
    orderNo?: string;
    /** The order's old single internal note (order_items.internal_comment), shown once at the top. */
    legacyNote?: unknown;
    pollMs?: number;
    /** AB#22133 — a message to start the box with (the CSM queue's Talk to). */
    draft?: string;
    /** The people that message mentions. */
    draftPeople?: { id: number; name: string }[];
  }>(),
  { orderNo: '', legacyNote: null, pollMs: 5000, draft: '', draftPeople: () => [] }
);
const emit = defineEmits<{ (e: 'sent'): void }>();

const store = useMainStore();
const meId = computed<number | null>(() => (store.user?.id ? Number(store.user.id) : null));
const features = computed(() => (store.features ?? {}) as Record<string, unknown>);
const canWrite = computed(() => features.value.team_chat_writing === true);
const canTranslate = computed(() => features.value.team_chat_translate !== false);

const {
  state,
  loading,
  sending,
  error,
  load,
  poll,
  loadOlder,
  send,
  edit,
  remove,
  markRead,
  setFollowing,
  typing,
  files,
  fileUrl,
  translateMessage,
  dismiss,
  move,
  exportUrl,
} = useTeamChat(props.orderId);

const scroller = ref<HTMLElement | null>(null);
const composer = ref<InstanceType<typeof TeamChatComposer> | null>(null);
const replyTo = ref<TcMessage | null>(null);
const atBottom = ref(true);
const newBelow = ref(0);
/** The read line as it was when the chat opened: "N new" stays put while you read. */
const readLine = ref<number | null>(null);

const shownMembers = computed(() =>
  (state.value?.members ?? [])
    .filter((m) => m.following)
    .sort((a, b) => Number(b.online) - Number(a.online))
    .slice(0, 6)
);
const moreMembers = computed(() =>
  Math.max(
    0,
    (state.value?.members ?? []).filter((m) => m.following).length - shownMembers.value.length
  )
);
const names = computed(() => {
  const set = new Set<string>();
  for (const m of state.value?.members ?? []) set.add(m.name);
  for (const m of state.value?.messages ?? []) if (m.user?.name) set.add(m.user.name);
  return [...set];
});
const typingText = computed(() => {
  const t = state.value?.typing ?? [];
  if (t.length === 1) return `${t[0]} is typing…`;
  if (t.length === 2) return `${t[0]} and ${t[1]} are typing…`;
  return 'Several people are typing…';
});

const legacy = computed(() => {
  let raw: unknown = props.legacyNote;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      raw = { comment: raw };
    }
  }
  const o = raw as { comment?: string; user_name?: string; created_at?: string } | null;
  if (!o?.comment?.trim()) return null;
  return {
    text: o.comment.trim(),
    by: o.user_name ?? '',
    at: o.created_at
      ? new Date(o.created_at).toLocaleDateString([], {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '',
  };
});

type Row =
  | { type: 'day'; key: string; label: string }
  | { type: 'new'; key: string; label: string }
  | { type: 'msg'; key: string; message: TcMessage; actionFindingIds: number[] };

const rows = computed<Row[]>(() => {
  const out: Row[] = [];
  const msgs = state.value?.messages ?? [];
  let lastDay = '';
  let newShown = false;
  const seenFinding = new Set<number>();
  const line = readLine.value ?? 0;
  const unreadCount = msgs.filter(
    (m) => m.id > line && m.kind !== 'system' && m.user?.id !== meId.value
  ).length;
  for (const m of msgs) {
    const day = new Date(m.created_at).toDateString();
    if (day !== lastDay) {
      out.push({ type: 'day', key: `d-${day}`, label: dayLabel(m.created_at) });
      lastDay = day;
    }
    if (
      !newShown &&
      line > 0 &&
      m.id > line &&
      m.user?.id !== meId.value &&
      m.kind !== 'system' &&
      unreadCount > 0
    ) {
      out.push({ type: 'new', key: 'new', label: `${unreadCount} new` });
      newShown = true;
    }
    const actionFindingIds: number[] = [];
    for (const f of m.flags) {
      if (f.finding_id && !seenFinding.has(f.finding_id)) {
        seenFinding.add(f.finding_id);
        actionFindingIds.push(f.finding_id);
      }
    }
    out.push({ type: 'msg', key: `m-${m.id}`, message: m, actionFindingIds });
  }
  return out;
});

/* ---- scrolling and reading ---- */
const scrollToBottom = async (smooth = false): Promise<void> => {
  await nextTick();
  const el = scroller.value;
  if (!el) return;
  try {
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  } catch {
    el.scrollTop = el.scrollHeight;
  }
  newBelow.value = 0;
  atBottom.value = true;
  void markRead();
};
const onScroll = (): void => {
  const el = scroller.value;
  if (!el) return;
  atBottom.value = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  if (atBottom.value) {
    newBelow.value = 0;
    void markRead();
  }
};

watch(
  () => state.value?.messages.length ?? 0,
  async (n, before) => {
    if (!before) return;
    const added = n - before;
    if (added <= 0) return;
    if (atBottom.value) await scrollToBottom(true);
    else newBelow.value += added;
  }
);

/* ---- actions ---- */
const onSend = async (payload: {
  body: string;
  files: File[];
  replyToId: number | null;
  portionId: number | null;
  mentions: number[];
}): Promise<void> => {
  if (await send(payload)) {
    composer.value?.reset();
    replyTo.value = null;
    await scrollToBottom(true);
    emit('sent');
  }
};
const onEdit = async (id: number, body: string): Promise<void> => {
  if (!(await edit(id, body)) && error.value) showToast(error.value, 'error');
};
const onDelete = async (id: number): Promise<void> => {
  if (await remove(id)) showToast('Message removed.', 'success');
  else if (error.value) showToast(error.value, 'error');
};
const onDismiss = async (findingId: number): Promise<void> => {
  if (await dismiss(findingId)) showToast('Noted — thanks. ELSA learns from this.', 'success');
};
// Move: when ELSA named the order it is one click; when she did not, a drawer
// asks for the order number (never a browser prompt) and shows what will move.
const moveAsk = ref<{ findingId: number; orderNo: string; error: string | null } | null>(null);
const moving = ref(false);
const moveInput = ref<HTMLInputElement | null>(null);
watch(
  () => moveAsk.value?.findingId,
  async (id) => {
    if (!id) return;
    await nextTick();
    moveInput.value?.focus();
  }
);
const moveMessages = computed<TcMessage[]>(() => {
  const id = moveAsk.value?.findingId;
  if (!id || !state.value) return [];
  return state.value.messages.filter((m) => (m.flags ?? []).some((f) => f.finding_id === id));
});
const moveTarget = computed(() => (moveAsk.value?.orderNo ?? '').trim().replace(/^#/, ''));
const onMove = async (findingId: number, orderNo: string | null): Promise<void> => {
  if (!orderNo) {
    moveAsk.value = { findingId, orderNo: '', error: null };
    return;
  }
  if (await move(findingId)) showToast(`Moved to #${orderNo}.`, 'success');
  else if (error.value) showToast(error.value, 'error');
};
const confirmMove = async (): Promise<void> => {
  const ask = moveAsk.value;
  if (!ask || !moveTarget.value || moving.value) return;
  moving.value = true;
  ask.error = null;
  const ok = await move(ask.findingId, moveTarget.value);
  moving.value = false;
  if (ok) {
    showToast(`Moved to #${moveTarget.value}.`, 'success');
    moveAsk.value = null;
  } else {
    ask.error = error.value ?? 'The messages were not moved.';
    error.value = null; // shown in the drawer, not under the chat as well
  }
};
const openFile = async (f: TcFile): Promise<void> => {
  const url = await fileUrl(f.id);
  if (url) window.open(url, '_blank', 'noopener');
  else showToast('The file could not be opened.', 'error');
};
const older = async (): Promise<void> => {
  const el = scroller.value;
  const from = el ? el.scrollHeight - el.scrollTop : 0;
  await loadOlder();
  await nextTick();
  if (el) el.scrollTop = el.scrollHeight - from;
};

const filesOpen = ref(false);
const filesLoading = ref(false);
const fileList = ref<TcFile[]>([]);
const openFiles = async (): Promise<void> => {
  filesOpen.value = true;
  filesLoading.value = true;
  try {
    fileList.value = await files();
  } finally {
    filesLoading.value = false;
  }
};
const doExport = async (): Promise<void> => {
  const url = await exportUrl();
  if (!url) {
    showToast('The export did not work.', 'error');
    return;
  }
  const a = document.createElement('a');
  a.href = url;
  a.download = `team-chat-order-${state.value?.order_no || props.orderId}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};

/* ---- polling ---- */
let timer: ReturnType<typeof setInterval> | null = null;
const tick = (): void => {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
  void poll();
};

onMounted(async () => {
  await load();
  readLine.value = state.value?.me.last_read_message_id ?? 0;
  await scrollToBottom();
  if (props.draft) {
    await nextTick();
    composer.value?.prefill(
      props.draft,
      props.draftPeople.map((p) => ({
        id: p.id,
        name: p.name,
        initials: p.name
          .split(/\s+/)
          .map((w) => w[0] ?? '')
          .join('')
          .slice(0, 2)
          .toUpperCase(),
        role: null,
      }))
    );
  }
  timer = setInterval(tick, props.pollMs);
});
onBeforeUnmount(() => {
  if (timer) clearInterval(timer);
});
</script>

<style scoped>
.tct {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  flex: 1;
  position: relative;
}
.tct-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  border-bottom: 1px solid var(--border-soft);
}
.tct-title {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 200px;
}
.tct-title h2 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
}
.tct-sub {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  color: var(--ink-mute);
}
.tct-people {
  display: flex;
  align-items: center;
}
.tct-person {
  position: relative;
  margin-left: -6px;
  border-radius: 50%;
  box-shadow: 0 0 0 2px var(--surface);
}
.tct-online {
  position: absolute;
  right: -1px;
  bottom: -1px;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--success);
  box-shadow: 0 0 0 2px var(--surface);
}
.tct-more {
  margin-left: 6px;
  font-size: 11.5px;
  color: var(--ink-mute);
}
.tct-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface-2);
  color: var(--ink-soft);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}
.tct-chip:hover {
  border-color: var(--accent);
  color: var(--ink);
}
.tct-chip--on {
  color: var(--accent);
  border-color: color-mix(in srgb, var(--accent) 45%, var(--border));
  background: var(--accent-soft);
}
.tct-list {
  flex: 1;
  min-height: 240px;
  overflow-y: auto;
  padding: 12px 12px 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.tct-empty {
  margin: 24px auto;
  max-width: 420px;
  text-align: center;
  font-size: 13px;
  color: var(--ink-mute);
  line-height: 1.6;
}
.tct-err {
  color: var(--danger);
  font-size: 12.5px;
  margin: 0 0 6px;
}
.tct-older {
  align-self: center;
  border: 0;
  background: none;
  color: var(--accent);
  font-size: 12px;
  cursor: pointer;
  padding: 6px;
}
.tct-day,
.tct-new {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 11px;
  color: var(--ink-mute);
  margin: 8px 0 4px;
}
.tct-day::before,
.tct-day::after,
.tct-new::before,
.tct-new::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--border-soft);
}
.tct-new {
  color: var(--accent);
  font-weight: 600;
}
.tct-new::before,
.tct-new::after {
  background: var(--accent);
}
.tct-new::after {
  flex: 0 0 40px;
}
.tct-legacy {
  border: 1px dashed var(--border);
  border-radius: 10px;
  padding: 10px 12px;
  margin: 4px 8px 8px;
  font-size: 12.5px;
  color: var(--ink-soft);
}
.tct-legacy-h {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-mute);
  margin-bottom: 4px;
}
.tct-legacy p {
  margin: 0 0 4px;
  color: var(--ink);
  white-space: pre-wrap;
}
.tct-typing {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11.5px;
  color: var(--ink-mute);
  padding: 4px 8px;
}
.tct-dots {
  display: inline-flex;
  gap: 3px;
}
.tct-dots i {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--ink-mute);
  animation: tct-blink 1.2s infinite ease-in-out;
}
.tct-dots i:nth-child(2) {
  animation-delay: 0.2s;
}
.tct-dots i:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes tct-blink {
  0%,
  80%,
  100% {
    opacity: 0.3;
  }
  40% {
    opacity: 1;
  }
}
@media (prefers-reduced-motion: reduce) {
  .tct-dots i {
    animation: none;
  }
}
.tct-jump {
  position: absolute;
  left: 50%;
  bottom: 150px;
  transform: translateX(-50%);
  border: 0;
  border-radius: 999px;
  background: var(--accent);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 14px;
  cursor: pointer;
  box-shadow: var(--shadow-2, 0 10px 30px rgba(0, 0, 0, 0.3));
}
.tct-foot {
  padding: 8px 20px 16px;
  /* Room for the Ask Atlas launcher bottom-right. */
  padding-right: max(20px, 92px);
}
.tct-readonly {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px dashed var(--border);
  border-radius: 11px;
  background: var(--surface);
  padding: 12px 14px;
  color: var(--ink-mute);
}
.tct-readonly div {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.tct-readonly b {
  font-size: 13px;
  color: var(--ink);
}
.tct-readonly span {
  font-size: 12px;
  color: var(--ink-soft);
}
.tct-move-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
  margin-bottom: 6px;
}
.tct-move-input {
  width: 100%;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--ink);
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 14px;
}
.tct-move-input:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 1px;
}
.tct-move-hint {
  margin: 10px 0 14px;
  font-size: 12.5px;
  line-height: 1.5;
  color: var(--ink-soft);
}
.tct-move-list {
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: 10px;
  overflow: hidden;
}
.tct-move-list li {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 9px 12px;
  font-size: 13px;
  border-top: 1px solid var(--border-soft);
  overflow-wrap: anywhere;
}
.tct-move-list li:first-child {
  border-top: 0;
}
.tct-move-list b {
  font-size: 12px;
  color: var(--ink-soft);
  font-weight: 600;
}
.tct-move-error {
  margin: 12px 0 0;
  font-size: 12.5px;
  color: var(--danger);
  background: var(--danger-soft);
  border-radius: 8px;
  padding: 8px 12px;
}
.tct-filelist {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.tct-fileitem {
  width: 100%;
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto;
  column-gap: 10px;
  align-items: center;
  text-align: left;
  padding: 8px 10px;
  border: 1px solid var(--border);
  border-radius: 9px;
  background: var(--surface);
  color: var(--ink-soft);
  cursor: pointer;
}
.tct-fileitem:hover {
  border-color: var(--accent);
}
.tct-fileitem svg {
  grid-row: span 2;
}
.tct-filename {
  font-size: 13px;
  color: var(--ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tct-filemeta {
  font-size: 11.5px;
  color: var(--ink-mute);
}
</style>
